// Form fields for the Add/Edit Variation dialog. Layout:
//   • Name (full-width, required)
//   • Number + Status (two-column row) — Number uses AutoIdentifierField
//   • Effective date + Value impact (two-column). Value impact carries
//     a `$` prefix and accepts positive AND negative integers
//     (descopes). Below the value: `[ ] Non-financial variation`
//     checkbox — when checked, the value field is disabled and the
//     payload persists `null`. This block precedes targets so that when
//     the value is entered, allocation rows surface in flow.
//   • Targets — multi-target picker. Apply-to-contract checkbox + SOW
//     chip-grid. Optional per-target allocation inputs surface only when
//     a value impact is set. The picker also shows a live sum-vs-total
//     footer when at least one allocation is non-empty. On a decisioned
//     (approved/rejected) edit, membership is frozen via
//     `membershipLocked` but the allocation split stays editable — only
//     `disabled` (a fully read-only render) freezes allocations too.
//   • Description (textarea)
//   • Advanced collapsible: Evidence links + Notes
//
// The form fields are stateless — state lives in the parent (modal or
// inline expand). On every change the parent re-validates; this
// component renders errors inline.

import { useId, useState, type ChangeEvent } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import EvidenceLinksField from "../shared/EvidenceLinksField";
import AutoIdentifierField from "../shared/AutoIdentifierField";
import CurrencyInput from "../shared/CurrencyInput";
import VariationTargetPicker from "./VariationTargetPicker";
import { gstSuffix } from "../../../../utils/gstSuffix";
import type { Sow, VariationStatus } from "../../../../types";
import { pastDateWarning } from "./pastDateWarning";
import {
  parseValueImpact,
  type VariationFieldErrors,
  type VariationFormState,
} from "./variationFormState";

const STATUSES: { value: VariationStatus; label: string }[] = [
  { value: "proposed", label: "Proposed" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

export type VariationFormMode = "create" | "edit";

export interface VariationFormFieldsProps {
  value: VariationFormState;
  onChange: (patch: Partial<VariationFormState>) => void;
  errors?: VariationFieldErrors;
  disabled?: boolean;
  idPrefix: string;
  mode: VariationFormMode;
  /** GST flag from the parent contract — drives the basis suffix on the
   *  Value impact input and the per-target allocation inputs. */
  gstApplies: boolean | undefined;
  /** SOWs available as variation targets. The picker filters cancelled
   *  / archived at render time. */
  sows: Sow[];
  /** Async uniqueness check for manual-override numbers. Optional in
   *  edit mode where the field is read-only. */
  validateUniqueNumber?: (next: string) => Promise<string | null>;
  /** Source variation status — only meaningful in edit mode. Used to
   *  status-gate the value-impact + target-picker controls: both are
   *  editable while the variation is `proposed` and locked once
   *  `approved` or `rejected`, mirroring the backend handler's
   *  status-gated PUT. Without this gate the user could edit a target
   *  list, hit Save, and receive a 409 — the frontend should preempt
   *  that interaction. */
  sourceStatus?: VariationFormState["status"];
}

export default function VariationFormFields({
  value,
  onChange,
  errors = {},
  disabled,
  idPrefix,
  mode,
  gstApplies,
  sows,
  validateUniqueNumber,
  sourceStatus,
}: VariationFormFieldsProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const labelId = useId();

  // Lock value-impact, non-financial flag, and target *membership* when
  // editing a variation that has already been decisioned. Approved and
  // rejected variations carry their target list and value impact as
  // part of the audit trail; mutating either post-decision would let the
  // stored row drift away from what was actually approved. The
  // allocation *split* across those frozen targets stays editable —
  // membershipLocked on the picker freezes only membership, not
  // allocations — since re-partitioning the already-approved total
  // between the same targets doesn't change what was approved.
  const decisionLocked =
    mode === "edit" &&
    (sourceStatus === "approved" || sourceStatus === "rejected");

  const patchString =
    <K extends keyof VariationFormState>(field: K) =>
    (
      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
    ) =>
      onChange({ [field]: e.target.value } as Pick<VariationFormState, K>);

  const isEdit = mode === "edit";
  const basis = gstSuffix(gstApplies);
  const valueImpactNumber = value.nonFinancial
    ? null
    : parseValueImpact(value.valueImpact);

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor={`${idPrefix}-name`} className="label">
          Name <span className="text-red-600">*</span>
        </label>
        <input
          id={`${idPrefix}-name`}
          type="text"
          className="input"
          value={value.name}
          onChange={patchString("name")}
          disabled={disabled}
          aria-invalid={!!errors.name}
          aria-describedby={
            errors.name ? `${idPrefix}-name-error` : undefined
          }
        />
        {errors.name && (
          <p
            id={`${idPrefix}-name-error`}
            role="alert"
            className="text-caption text-red-600 dark:text-red-400 mt-1"
          >
            {errors.name}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <span id={`${idPrefix}-number-label`} className="label">
            Variation number
          </span>
          <AutoIdentifierField
            value={value.number}
            label="Variation number"
            editable={!isEdit}
            onCommit={(next) =>
              onChange({
                number: next,
                numberOverrideMode: !isEdit && next.trim().length > 0,
              })
            }
            validateUnique={
              isEdit
                ? undefined
                : (next) =>
                    next.trim().length === 0 || !validateUniqueNumber
                      ? Promise.resolve(null)
                      : validateUniqueNumber(next)
            }
            helperText={
              isEdit
                ? "Numbers are immutable once a variation is created."
                : value.numberOverrideMode
                  ? "Manual override. Format: VAR-YYYY-NNN. Must be unique within this contract."
                  : "Auto-generated. Click Edit to use a different number."
            }
            externalError={errors.number ?? null}
            testId={`${idPrefix}-number`}
            disabled={disabled}
          />
        </div>

        <div>
          <label htmlFor={`${idPrefix}-status`} className="label">
            Status
          </label>
          <select
            id={`${idPrefix}-status`}
            className="input"
            value={value.status}
            onChange={(e) =>
              onChange({ status: e.target.value as VariationStatus })
            }
            disabled={disabled || isEdit}
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          {isEdit && (
            <p className="text-caption text-slate-500 dark:text-slate-400 mt-1">
              Status transitions go through the Approve / Reject actions
              on this row.
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${idPrefix}-effective`} className="label">
            Effective date
          </label>
          <input
            id={`${idPrefix}-effective`}
            type="date"
            className="input"
            value={value.effectiveDate}
            onChange={patchString("effectiveDate")}
            disabled={disabled}
            aria-invalid={!!errors.effectiveDate}
          />
          {errors.effectiveDate && (
            <p
              role="alert"
              className="text-caption text-red-600 dark:text-red-400 mt-1"
            >
              {errors.effectiveDate}
            </p>
          )}
          {!errors.effectiveDate && pastDateWarning(value.effectiveDate) && (
            <p
              data-testid={`${idPrefix}-effective-past-warning`}
              className="text-caption text-amber-700 dark:text-amber-400 mt-1"
            >
              {pastDateWarning(value.effectiveDate)}
            </p>
          )}
        </div>
        <div>
          <label htmlFor={`${idPrefix}-value-impact`} className="label">
            Value impact
          </label>
          <div className="relative max-w-[240px]">
            <span
              aria-hidden="true"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400"
            >
              $
            </span>
            <CurrencyInput
              id={`${idPrefix}-value-impact`}
              value={value.nonFinancial ? "" : value.valueImpact}
              onChange={(raw) => onChange({ valueImpact: raw })}
              allowNegative
              allowDecimal
              ariaLabel="Value impact"
              ariaInvalid={!!errors.valueImpact}
              ariaDescribedBy={`${labelId}-basis ${labelId}-helper`}
              disabled={disabled || value.nonFinancial || decisionLocked}
              placeholder="0"
              testId={`${idPrefix}-value-impact`}
            />
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span
              id={`${labelId}-basis`}
              data-testid={`${idPrefix}-value-impact-basis`}
              className="text-caption text-slate-500 dark:text-slate-400"
            >
              {basis}
            </span>
          </div>
          <p
            id={`${labelId}-helper`}
            className="text-caption text-slate-500 dark:text-slate-400 mt-1"
          >
            {decisionLocked
              ? "Value impact cannot be changed on an approved or rejected variation. Status transitions go through Approve / Reject."
              : "Negative values represent descopes. Use the Non-financial checkbox for variations with no monetary effect."}
          </p>
          {errors.valueImpact && (
            <p
              role="alert"
              className="text-caption text-red-600 dark:text-red-400 mt-1"
            >
              {errors.valueImpact}
            </p>
          )}
          <label className="inline-flex items-center gap-2 mt-2 text-body-sm text-slate-700 dark:text-slate-200 cursor-pointer">
            <input
              type="checkbox"
              checked={value.nonFinancial}
              onChange={(e) =>
                onChange({
                  nonFinancial: e.target.checked,
                  // Clear the value field when toggling on so a stale
                  // figure can't sneak through; preserved on toggle off.
                  valueImpact: e.target.checked ? "" : value.valueImpact,
                })
              }
              disabled={disabled || decisionLocked}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-400 dark:border-slate-600 dark:bg-slate-800"
              data-testid={`${idPrefix}-non-financial`}
            />
            Non-financial variation
          </label>
        </div>
      </div>

      <div>
        <VariationTargetPicker
          value={value.targets}
          onChange={(next) => onChange({ targets: next })}
          sows={sows}
          valueImpact={valueImpactNumber}
          gstApplies={gstApplies}
          disabled={disabled}
          membershipLocked={decisionLocked}
          idPrefix={idPrefix}
        />
        {errors.targets && (
          <p
            role="alert"
            className="text-caption text-red-600 dark:text-red-400 mt-1"
            data-testid={`${idPrefix}-targets-error`}
          >
            {errors.targets}
          </p>
        )}
      </div>

      <div>
        <label htmlFor={`${idPrefix}-description`} className="label">
          Description
        </label>
        <textarea
          id={`${idPrefix}-description`}
          className="input min-h-[80px]"
          value={value.description}
          onChange={patchString("description")}
          disabled={disabled}
        />
      </div>

      <div>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-body-sm text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-slate-50 focus:outline-hidden focus:ring-2 focus:ring-brand-400 rounded-sm"
          onClick={() => setAdvancedOpen((v) => !v)}
          aria-expanded={advancedOpen}
        >
          {advancedOpen ? (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          )}
          Advanced
        </button>
        {advancedOpen && (
          <div className="mt-3 space-y-4">
            <EvidenceLinksField
              value={value.evidenceLinks}
              onChange={(links) => onChange({ evidenceLinks: links })}
              disabled={disabled}
            />
            <div>
              <label htmlFor={`${idPrefix}-notes`} className="label">
                Notes
              </label>
              <textarea
                id={`${idPrefix}-notes`}
                className="input min-h-[80px]"
                value={value.notes}
                onChange={patchString("notes")}
                disabled={disabled}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
